import React, { useCallback } from "react";
import { FaQuestionCircle } from "react-icons/fa";
import PostCard from "../profile/PostCard";
import api from "../../utils/api/axiosInstance";
import { filterPostsForAskFeed } from "../../utils/feedPostTypeFilters";
import { useFeedDedicatedTab } from "../../hooks/useFeedDedicatedTab";
import FeedTabErrorState from "./FeedTabErrorState";
import styles from "../../styles/pages/feed/FeedPage.module.css";

const FeedAskTab = ({
  active,
  activeFilter = "forYou",
  onCreateAskPost,
  onOpenPost,
  onPostDeleted,
  onLikeUpdate,
  onSaveUpdate,
  onCommentUpdate,
  onVoteUpdate,
  onPostUpdated,
  onEditPost,
  onCommentOpen,
  activeDecision = null,
  shopperCategoryKeys = null,
  scrollRoot = null,
}) => {
  const fetchPage = useCallback(async (pageNum) => {
    const res = await api.get("/posts/ask", {
      params: { page: pageNum, limit: 10, filter: activeFilter },
    });
    return {
      items: filterPostsForAskFeed(res.data.posts || []),
      hasMore: res.data.pagination?.hasMore ?? false,
    };
  }, [activeFilter]);

  const {
    items: askPosts,
    setItems: setAskPosts,
    loading,
    error,
    hasMore,
    loadMoreRef,
    retry,
  } = useFeedDedicatedTab({
    active,
    fetchPage,
    errorLabel: "questions",
    scrollRoot,
  });

  const handlePostDeleted = useCallback(
    (postId) => {
      setAskPosts((prev) => prev.filter((p) => String(p._id) !== String(postId)));
      onPostDeleted?.(postId);
    },
    [onPostDeleted, setAskPosts],
  );

  const handlePostUpdated = useCallback(
    (updatedPost) => {
      setAskPosts((prev) =>
        prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)),
      );
      onPostUpdated?.(updatedPost);
    },
    [onPostUpdated, setAskPosts],
  );

  if (!active) return null;

  if (loading && askPosts.length === 0) {
    return (
      <div className={styles.skeletonList} aria-hidden="true">
        {[0, 1, 2].map((idx) => (
          <div key={idx} className={styles.skeletonCard}>
            <div className={styles.skeletonHeader}>
              <span className={styles.skeletonAvatar} />
              <div className={styles.skeletonHeaderText}>
                <span className={styles.skeletonLineShort} />
              </div>
            </div>
            <div className={styles.skeletonContent}>
              <span className={styles.skeletonLineMedium} />
              <span className={styles.skeletonLineFull} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && askPosts.length === 0) {
    return (
      <FeedTabErrorState
        title="Could not load questions"
        message={error}
        onRetry={retry}
      />
    );
  }

  if (askPosts.length === 0) {
    const isFollowing = activeFilter === "following";
    return (
      <div className={styles.emptyState}>
        <FaQuestionCircle className={styles.emptyIcon} />
        <h2>
          {isFollowing
            ? "No questions from people you follow"
            : "No questions yet"}
        </h2>
        <p>
          {isFollowing
            ? "Follow more people to see their questions here."
            : "Ask the community for product advice, comparisons, or buying help."}
        </p>
        <button
          type="button"
          className={styles.exploreButton}
          onClick={onCreateAskPost}
        >
          Ask a Question
        </button>
      </div>
    );
  }

  return (
    <div className={styles.postsContainer}>
      {askPosts.map((post) => (
        <div
          key={post._id}
          role="button"
          tabIndex={0}
          className={styles.postClickableWrapper}
          onClick={(event) => onOpenPost?.(post, event)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            const tag = e.target?.tagName;
            if (
              tag === "INPUT" ||
              tag === "TEXTAREA" ||
              tag === "SELECT" ||
              e.target?.isContentEditable
            ) {
              return;
            }
            e.preventDefault();
            onOpenPost?.(post, e);
          }}
        >
          <PostCard
            post={post}
            activeDecision={activeDecision}
            shopperCategoryKeys={shopperCategoryKeys}
            onDelete={handlePostDeleted}
            onLikeUpdate={onLikeUpdate}
            onSaveUpdate={onSaveUpdate}
            onCommentUpdate={onCommentUpdate}
            onVoteUpdate={onVoteUpdate}
            onPostUpdated={handlePostUpdated}
            onEdit={() => onEditPost?.(post)}
            commentAction="modal"
            onCommentNavigate={() => onCommentOpen?.(post)}
            disableCardClick={true}
          />
        </div>
      ))}
      {hasMore && (
        <div ref={loadMoreRef} className={styles.loadMoreTrigger} />
      )}
    </div>
  );
};

export default React.memo(FeedAskTab);
